import React, { useState, useEffect } from 'react';
import { 
  Activity, Zap, Clock, TrendingUp, AlertTriangle, 
  CheckCircle, XCircle, BarChart3, Database, Server
} from 'lucide-react';
import { 
  LineChart, BarChart, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const MetricCard = ({ title, value, subtitle, icon, color = 'blue', trend }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
        {trend && (
          <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      <h3 className="text-sm font-semibold text-gray-600 uppercase mb-1">{title}</h3>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
};

const MetricsDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchMetrics();
    
    if (autoRefresh) {
      const interval = setInterval(fetchMetrics, 5000); // Auto-refresh 5s
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/metrics/dashboard', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { hours: 24 }
      });
      
      setMetrics(response.data);
      setLastUpdate(new Date());
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao buscar métricas:', err);
      setError(err.response?.data?.error || 'Falha ao carregar métricas');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Activity className="w-16 h-16 mx-auto mb-4 animate-pulse text-blue-500" />
            <p className="text-gray-600 text-lg">Carregando métricas...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
          <h2 className="text-xl font-bold text-red-800 mb-2">Erro ao carregar métricas</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchMetrics}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (!metrics) {
    return null;
  }

  const { llm, queue, period } = metrics;

  // Calcula alertas
  const alerts = [];
  if (llm.realTime.rpm > 40) {
    alerts.push({ type: 'warning', message: `RPM alto: ${llm.realTime.rpm.toFixed(1)} (próximo do limite de 50)` });
  }
  if (llm.realTime.successRate < 95) {
    alerts.push({ type: 'error', message: `Taxa de sucesso baixa: ${llm.realTime.successRate.toFixed(1)}%` });
  }
  if (queue.current?.messagesReady > 10) {
    alerts.push({ type: 'warning', message: `${queue.current.messagesReady} mensagens aguardando na fila` });
  }
  if (queue.current?.messagesDLQ > 0) {
    alerts.push({ type: 'error', message: `${queue.current.messagesDLQ} mensagens falhadas na DLQ (requer análise)` });
  }

  // Prepara dados para gráfico de distribuição por provider (baseado no período completo, não apenas tempo real)
  const providerData = llm.summary.requestsByProvider 
    ? Object.entries(llm.summary.requestsByProvider).map(([provider, count]) => ({
        name: provider,
        value: count,
        percentage: ((count / llm.summary.totalRequests) * 100).toFixed(1)
      }))
    : [];

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-500" />
              Métricas LLM & Fila
            </h1>
            <p className="text-gray-600 mt-2">
              Período: últimas {period.hours}h | 
              Última atualização: {lastUpdate?.toLocaleTimeString('pt-BR')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Auto-refresh (5s)
            </label>
            <button
              onClick={fetchMetrics}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                       transition-colors flex items-center gap-2 shadow-md"
            >
              <Zap className="w-4 h-4" />
              Atualizar
            </button>
          </div>
        </div>
      </div>

      {/* Alertas */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`p-4 rounded-lg flex items-center gap-3 shadow-sm ${
                alert.type === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
              }`}
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Real-Time Metrics Cards */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">
          <Zap className="w-5 h-5 text-orange-500" />
          Métricas em Tempo Real (Últimos 5 min)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="RPM"
            value={llm.realTime.rpm.toFixed(1)}
            subtitle={`${llm.realTime.totalRequests} requests`}
            icon={<Activity className="w-6 h-6" />}
            color="blue"
          />
          <MetricCard
            title="TPM"
            value={llm.realTime.tpm.toFixed(0)}
            subtitle="Tokens/minuto"
            icon={<TrendingUp className="w-6 h-6" />}
            color="green"
          />
          <MetricCard
            title="Latência Média"
            value={`${(llm.realTime.avgLatencyMs / 1000).toFixed(2)}s`}
            subtitle={`${llm.realTime.avgLatencyMs.toFixed(0)}ms`}
            icon={<Clock className="w-6 h-6" />}
            color="purple"
          />
          <MetricCard
            title="Taxa de Sucesso"
            value={`${llm.realTime.successRate.toFixed(1)}%`}
            subtitle={`${llm.realTime.totalRequests} total`}
            icon={llm.realTime.successRate >= 95 ? <CheckCircle className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
            color={llm.realTime.successRate >= 95 ? 'green' : 'red'}
          />
        </div>
      </div>

      {/* Resumo do Período */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">
          <BarChart3 className="w-5 h-5 text-blue-500" />
          Resumo do Período ({period.hours}h)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total de Requests"
            value={llm.summary.totalRequests.toLocaleString('pt-BR')}
            subtitle={`${llm.summary.successfulRequests} sucesso / ${llm.summary.failedRequests} falha`}
            icon={<Database className="w-6 h-6" />}
            color="blue"
          />
          <MetricCard
            title="Total de Tokens"
            value={(llm.summary.totalTokens / 1000).toFixed(1) + 'K'}
            subtitle={`${(llm.summary.totalInputTokens / 1000).toFixed(1)}K input + ${(llm.summary.totalOutputTokens / 1000).toFixed(1)}K output`}
            icon={<TrendingUp className="w-6 h-6" />}
            color="green"
          />
          <MetricCard
            title="Latência Média"
            value={`${(llm.summary.avgLatencyMs / 1000).toFixed(2)}s`}
            subtitle="Período completo"
            icon={<Clock className="w-6 h-6" />}
            color="purple"
          />
          <MetricCard
            title="Taxa de Sucesso"
            value={`${(llm.summary.successRate * 100).toFixed(1)}%`}
            subtitle={`${llm.summary.successfulRequests}/${llm.summary.totalRequests}`}
            icon={<CheckCircle className="w-6 h-6" />}
            color={llm.summary.successRate >= 0.95 ? 'green' : 'yellow'}
          />
        </div>
      </div>

      {/* Distribuição por Provider */}
      {providerData.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">
            <Server className="w-5 h-5 text-purple-500" />
            Distribuição por Provider ({period.hours}h)
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={providerData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {providerData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Detalhes por Provider</h3>
              <div className="space-y-3">
                {llm.summary.requestsByProvider && Object.entries(llm.summary.requestsByProvider).map(([provider, count]) => (
                  <div key={provider} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-semibold text-gray-800">{provider}</span>
                      <p className="text-sm text-gray-600">{count} requests ({((count / llm.summary.totalRequests) * 100).toFixed(1)}%)</p>
                    </div>
                    <div className="text-right">
                      {llm.summary.avgLatencyByProvider[provider] && (
                        <p className="text-sm text-gray-600">
                          {(llm.summary.avgLatencyByProvider[provider] / 1000).toFixed(2)}s
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gráfico de Histórico */}
      {llm.history && llm.history.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Histórico ({period.hours}h)
          </h2>
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={llm.history}>
                <defs>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(value) => new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleString('pt-BR')}
                  formatter={(value) => [value.toFixed(0), '']}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="requests" 
                  stroke="#3b82f6" 
                  fillOpacity={1} 
                  fill="url(#colorRequests)" 
                  name="Requests/hora"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Queue Metrics */}
      {queue.current && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">
            <Database className="w-5 h-5 text-yellow-500" />
            Status da Fila RabbitMQ
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              title="Mensagens Aguardando"
              value={queue.current.messagesReady}
              subtitle="Ready para processar"
              icon={<Server className="w-6 h-6" />}
              color={queue.current.messagesReady > 10 ? 'yellow' : 'green'}
            />
            <MetricCard
              title="Em Processamento"
              value={queue.current.messagesUnacknowledged}
              subtitle="Unacknowledged"
              icon={<Activity className="w-6 h-6" />}
              color="blue"
            />
            <MetricCard
              title="Dead Letter Queue"
              value={queue.current.messagesDLQ}
              subtitle="Mensagens falhadas"
              icon={<XCircle className="w-6 h-6" />}
              color={queue.current.messagesDLQ > 0 ? 'red' : 'green'}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MetricsDashboard;
